import React, { PropsWithChildren } from 'react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon, EllipsisHorizontalIcon, EllipsisVerticalIcon } from '@heroicons/react/20/solid';
import Utilities from '@ui/utils/Utilities';
import FormButtonStyleState from '@ui/button/FormButtonStyleState';
import { Link } from 'react-router-dom';
import SizeTranslator, { SizeType } from '@ui/styles/SizeTranslator';
import { TOOLBAR_BUTTON, TOOLBAR_BUTTON_DISABLED } from './toolbarButtonStyle';

type LinkItem = {
  name: string,
  disabled?: boolean
  link?: string,
  icon?: React.JSX.Element,
  onClick?: () => void,
  isSelected?: boolean
  badge?: React.JSX.Element,
  showSelectedCheck?: boolean,
  // 'danger': a red item, for an action that removes something.
  tone?: 'danger',
  // A second line under the name, in gray, that says what the action gives. Only with `toolbar`.
  detail?: string,
}

type Props = {
  items: LinkItem[],
  state?: FormButtonStyleState,
  portal?: boolean,
  className?: string
  extraButtonClassName?: string
  menuItemClassName?: string,
  hideArrow?: boolean,
  description?: string,
  disabled?: boolean,
  optionsSize?: SizeType,
  // 'links' (default): each item is a link. 'actions': each item is a button that runs its onClick and opens no
  // page; with no children, the button is a gray horizontal ellipsis (give it an ariaLabel).
  variant?: 'links' | 'actions',
  // The name of the button for a screen reader. Needed when the button shows only an icon.
  ariaLabel?: string,
  // The toolbar look, for a card header: the text button of ToolbarButton (36 px, a gray ring), its `icon` before the text
  // and a gray chevron, as CheckboxMenu variant 'button'. The menu has the look of the CheckboxMenu list (14 px text);
  // an item can show a `detail` line. Each item runs its onClick (as `variant: 'actions'`). Disabled: the button stays
  // with the gray colours of ToolbarButton, and the menu does not open. `state`, `className` and `hideArrow` do nothing.
  toolbar?: boolean,
  // The icon before the text of a toolbar button: give it the size only (h-4 w-4). The button sets its colour.
  icon?: React.ReactNode,
}

const ButtonLinkList: React.FunctionComponent<PropsWithChildren<Props>> = ({
                                                                             children,
                                                                             items,
                                                                             state = 'white',

                                                                             className,
                                                                             extraButtonClassName,
                                                                             description,
                                                                             menuItemClassName,
                                                                             hideArrow = false,
                                                                             disabled = false,
                                                                             optionsSize = 'large',
                                                                             variant = 'links',
                                                                             ariaLabel,
                                                                             toolbar = false,
                                                                             icon,
                                                                           }) => {

  const buttonState = disabled ? 'disabled' : state;
  const hasChildren = Boolean(children);
  const actions = variant === 'actions' || toolbar;

  if (toolbar) return <Menu as="div" className="relative inline-block text-left">
    <MenuButton disabled={disabled} aria-label={ariaLabel} className={disabled ? TOOLBAR_BUTTON_DISABLED : TOOLBAR_BUTTON}>
      {icon !== undefined && <span aria-hidden="true" className={disabled ? 'text-gray-300' : 'text-gray-400'}>{icon}</span>}
      {children}
      <ChevronDownIcon className={Utilities.classNames('-mr-1 h-4 w-4', disabled ? 'text-gray-300' : 'text-gray-400')} aria-hidden="true" />
    </MenuButton>
    {!disabled && <MenuItems anchor={{ to: 'bottom end', gap: 8, padding: 16 }}
      className="z-50 w-64 rounded-md bg-white py-1 text-left shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
      {(description != null && description !== '') &&
        <p className="border-b border-gray-100 px-3 pb-1.5 pt-1 text-xs font-normal text-gray-500">{description}</p>}
      {items.map((item) => (
        <MenuItem key={item.name} disabled={item.disabled === true}>
          {({ focus }) => <button type="button" onClick={item.onClick} disabled={item.disabled === true}
            className={Utilities.classNames('flex w-full items-start gap-x-2 px-3 py-2 text-left text-sm',
              item.disabled === true ? 'cursor-not-allowed text-gray-400' : 'cursor-pointer text-gray-700',
              item.disabled !== true && focus ? 'bg-gray-100 text-gray-900' : '')}>
            {item.icon != null && <span aria-hidden="true" className={Utilities.classNames('mt-0.5 shrink-0', item.disabled === true ? 'text-gray-300' : 'text-gray-400')}>{item.icon}</span>}
            <span className="min-w-0">
              <span className={Utilities.classNames('block', item.detail != null ? 'font-medium' : 'font-normal')}>{item.name}</span>
              {item.detail != null && <span className={Utilities.classNames('mt-0.5 block text-xs font-normal', item.disabled === true ? 'text-gray-400' : 'text-gray-500')}>{item.detail}</span>}
            </span>
          </button>}
        </MenuItem>
      ))}
    </MenuItems>}
  </Menu>;

  return <Menu as="div" className="relative inline-block text-left">
    <MenuButton disabled={disabled} aria-label={ariaLabel}
      className={hasChildren ? ((className != null && className !== '') ? className :
          Utilities.classNames(
            'cursor-pointer, ' +
            'btn',
            'btn-' + buttonState,
            disabled ? 'pointer-events-none' : '',
            extraButtonClassName ?? ''))
        : (actions ? 'rounded p-0.5 text-gray-400 hover:text-gray-600 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600' : '')}>
      {children}
      {hasChildren && !disabled && !hideArrow && <ChevronDownIcon className="-mr-1 ml-1 h-5 w-5" aria-hidden="true" />}
      {!hasChildren && (actions
        ? <EllipsisHorizontalIcon className="h-5 w-5" aria-hidden="true" />
        : <EllipsisVerticalIcon className="h-4 w-4" aria-hidden="true" />)}
    </MenuButton>

    {!disabled && <MenuItems anchor="bottom end"
                             className={`${actions ? 'z-50' : 'z-10'} -mr-1 ${SizeTranslator.translate(optionsSize)} origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none divide-y divide-gray-100 mt-2`}>
      {(description != null && description !== '') && <div className="px-4 py-3">
        <p className="text-xs text-gray-500">{description}</p>
      </div>}
      {!disabled && <div className="py-1">
        {items.map((item) => (
          <MenuItem key={item.name} disabled={actions && item.disabled === true}>
            {({ focus }) => {
              const danger = item.tone === 'danger';
              const look = Utilities.classNames(
                item.disabled !== true && focus ? (danger ? 'bg-red-50 text-red-700' : 'bg-gray-100 text-gray-900') : '',
                menuItemClassName ?? 'block px-4 py-2 text-xs cursor-pointer',
                item.disabled === true ? 'pointer-events-none text-gray-400' : (danger ? 'text-red-700' : 'text-gray-700'),
                item.isSelected === true ? 'bg-blue-50 text-blue-700' : '',
              );
              const content = <>
                {item.icon != null ? <div
                  className={Utilities.classNames('flex items-center gap-x-2', item.disabled === true ? 'opacity-60' : '')}>
                  {item.icon}
                  <span className="font-semibold block">{item.name}</span>
                  {item.badge}
                </div> : <span className="font-semibold block">{item.name}</span>}
                {item.isSelected === true && item.showSelectedCheck !== false && <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                    <CheckIcon className="h-5 w-5 text-green-500"/>
                </span>}
              </>;
              return actions
                ? <button type="button" onClick={item.onClick} disabled={item.disabled === true}
                  className={Utilities.classNames(look, 'w-full text-left')}>{content}</button>
                : <Link to={item.link!} onClick={item.onClick} className={look}>{content}</Link>;
            }}
          </MenuItem>
        ))}
      </div>}
    </MenuItems>}
  </Menu>;
};

export default ButtonLinkList;
